import Tracker from "../tracker.js";
import {EVENT, LOOPTYPE, TRACKERMODE} from "../enum.js";
import Note from "../models/note.js";
import Instrument from "../models/instrument.js";
import Sample from "../models/sample.js";
import EventBus from "../eventBus.js";

// Impulse Tracker (.it) module loader
// Supports: IT 1.xx/2.xx modules, multi-sample instruments with envelopes,
// 8/16/24-bit signed sample data (delta-encoded), pattern unpacking,
// volume column effects, and IT→XM effect mapping for playback.

var ImpulseTracker = function(){
	var me = {};

	me.load = function(file, name){
		console.log("loading ImpulseTracker");
		Tracker.setTrackerMode(TRACKERMODE.FASTTRACKER, true);
		Tracker.clearInstruments(1);

		file.litteEndian = true;

		var song = { patterns: [], instruments: [] };

		// ---- IT Header ----
		// Layout: sig(4) + name(26) + highlight(2) + ordnum(2) + insnum(2) +
		//         smpnum(2) + patnum(2) + cwtv(2) + cmwt(2) + flags(2) + special(2) +
		//         gv(1) + mv(1) + is(1) + it(1) + sep(1) + pwd(1) + msglen(2) + msgoff(4) + reserved(4)
		//         + chnpan(64) + chnvol(64) = 192 bytes before orders
		file.goto(0);
		var sig = file.readString(4); // "IMPM"
		if (sig !== "IMPM") {
			console.error("Not an IT file: bad signature " + sig);
			return song;
		}
		song.title = file.readString(26);
		song.sourceType = "IT";
		song.typeId = "IMPM";

		file.jump(2); // highlight minor/major
		var orderCount = file.readWord();       // offset 32
		var instrumentCount = file.readWord();   // offset 34
		var sampleCount = file.readWord();       // offset 36
		var patternCount = file.readWord();      // offset 38
		file.jump(4); // cwtv + cmwt (offset 40-43)
		var flags = file.readWord();             // offset 44
		var special = file.readWord();           // offset 46
		var globalVolume = file.readUbyte();     // offset 48
		var mixVolume = file.readUbyte();        // offset 49
		var initialSpeed = file.readUbyte();     // offset 50
		var initialTempo = file.readUbyte();     // offset 51
		file.jump(2); // sep + pwd (offset 52-53)
		var messageLength = file.readWord();     // offset 54
		var messageOffset = file.readDWord();    // offset 56
		file.jump(4); // reserved (offset 60-63)

		// Channel pan table (64 entries, offset 64)
		var channelPans = [];
		var channels = 0;
		for (var i = 0; i < 64; i++) {
			var pan = file.readUbyte();
			channelPans.push(pan);
			if (pan < 128) channels = i + 1;
		}

		// Channel volume table (64 entries, offset 128)
		var channelVols = [];
		for (i = 0; i < 64; i++) channelVols.push(file.readUbyte());

		// Orders start at offset 192
		var orders = [];
		for (i = 0; i < orderCount; i++) {
			var o = file.readUbyte();
			orders.push(o);
		}

		// Instrument parapointers
		var instrumentOffsets = [];
		for (i = 0; i < instrumentCount; i++) instrumentOffsets.push(file.readDWord());

		// Sample header parapointers
		var sampleHeaderOffsets = [];
		for (i = 0; i < sampleCount; i++) sampleHeaderOffsets.push(file.readDWord());

		// Pattern parapointers
		var patternOffsets = [];
		for (i = 0; i < patternCount; i++) patternOffsets.push(file.readDWord());

		// Build pattern table (skip skip markers 0xFE and end 0xFF)
		var patternTable = [];
		for (i = 0; i < orders.length; i++) {
			if (orders[i] === 255) break;
			if (orders[i] === 254) continue; // skip marker
			patternTable.push(orders[i]);
		}

		song.patternTable = patternTable;
		song.length = patternTable.length;
		song.channels = channels || 1;
		song.restartPosition = 0;

		// Channel panning: IT uses 0-64 for pan, 100 = surround
		song.channelPanning = [];
		for (i = 0; i < channels; i++) {
			var p = channelPans[i];
			if (p === 100) p = 32; // surround → center
			else if (p < 128) p = Math.round(p / 64 * 255); // 0-64 → 0-255
			else p = 128;
			song.channelPanning.push(p);
		}

		// ---- Read patterns ----
		for (i = 0; i < patternCount; i++) {
			song.patterns.push(readPattern(file, patternOffsets[i], song.channels));
		}

		// ---- Read sample headers and data ----
		// IT separates samples from instruments. Instruments reference samples.
		var samples = [];
		for (i = 0; i < sampleCount; i++) {
			samples.push(readSample(file, sampleHeaderOffsets[i]));
		}

		// ---- Read instruments ----
		var instrumentContainer = [];
		if (instrumentCount > 0) {
			for (i = 1; i <= instrumentCount; i++) {
				var inst = readInstrument(file, instrumentOffsets[i - 1], samples);
				Tracker.setInstrument(i, inst);
				instrumentContainer.push({ label: i + " " + inst.name, data: i });
			}
		} else {
			// No instruments — each sample becomes its own instrument
			for (i = 1; i <= sampleCount; i++) {
				var inst2 = Instrument();
				inst2.name = samples[i - 1].name;
				inst2.samples = [samples[i - 1]];
				inst2.sample = samples[i - 1];
				inst2.sampleNumberForNotes = [];
				for (var n = 0; n < 96; n++) inst2.sampleNumberForNotes.push(i - 1);
				inst2.setSampleIndex(0);
				Tracker.setInstrument(i, inst2);
				instrumentContainer.push({ label: i + " " + inst2.name, data: i });
			}
		}

		EventBus.trigger(EVENT.instrumentListChange, instrumentContainer);
		song.instruments = Tracker.getInstruments();

		// Set linear frequency based on IT flags (bit 3 = linear slides)
		Tracker.useLinearFrequency = (flags & 8) !== 0;

		// Set tempo
		if (initialSpeed && initialSpeed !== 255) Tracker.setAmigaSpeed(initialSpeed);
		if (initialTempo >= 33) Tracker.setBPM(initialTempo);

		return song;
	};

	// ---- Pattern reading ----
	// IT patterns are variable-length with row packing.
	function readPattern(file, offset, channelCount) {
		var patternData = [];
		var patternLength = 64;

		for (var step = 0; step < 256; step++) patternData.push(getEmptyRow(channelCount));

		if (!offset) {
			return patternData.slice(0, 64);
		}

		file.goto(offset);
		var length = file.readWord(); // packed pattern data length (excludes 8-byte header)
		var rows = file.readWord();   // number of rows in this pattern
		file.jump(4); // reserved

		patternLength = rows || 64;
		var end = file.index + length;
		var rowIndex = 0;

		// Per-channel state for mask variable carry-over
		var lastMask = [];     // last mask variable for each channel
		var lastNote = [];     // last note for each channel
		var lastInst = [];     // last instrument for each channel
		var lastVol = [];      // last volume/pan for each channel
		var lastEff = [];      // last effect for each channel
		var lastEffParam = []; // last effect param for each channel
		for (var i = 0; i < 64; i++) {
			lastMask[i] = 0;
			lastNote[i] = 0;
			lastInst[i] = 0;
			lastVol[i] = 0;
			lastEff[i] = 0;
			lastEffParam[i] = 0;
		}

		while (file.index < end && rowIndex < patternLength && rowIndex < 256) {
			var what = file.readUbyte();
			if (what === 0) {
				rowIndex++;
				continue;
			}

			// IT pattern variable byte:
			// Channel = (what - 1) & 63
			// Bit 7: new mask variable follows; else use previous mask for this channel
			var channel = (what - 1) & 63;
			var targetChannel = channel < channelCount ? channel : -1;

			var mask;
			if (what & 0x80) {
				mask = file.readUbyte();
				lastMask[channel] = mask;
			} else {
				mask = lastMask[channel] || 0;
			}

			var note = null;
			if (targetChannel >= 0) note = patternData[rowIndex][targetChannel];

			// Bit 0: read note
			if (mask & 1) {
				var noteValue = file.readUbyte();
				lastNote[channel] = noteValue;
				if (note) {
					// IT note: 0-119 = C-0 to B-9 (0-indexed!), 255 = note off, 254 = note cut
					if (noteValue === 255) {
						note.setIndex(97); // note off → XM note off
					} else if (noteValue === 254) {
						note.setIndex(97); // note cut → treat as note off
					} else if (noteValue <= 119) {
						// IT note 0 = C-0 → XM note 1 = C-0 (add 1)
						var xmNote = noteValue + 1;
						if (xmNote > 96) xmNote = 96; // cap at B-7
						note.setIndex(xmNote);
					}
				}
			}

			// Bit 4: use last note
			if ((mask & 16) && note) {
				var ln = lastNote[channel];
				if (ln === 255) {
					note.setIndex(97);
				} else if (ln === 254) {
					note.setIndex(97);
				} else if (ln <= 119) {
					var xmN = ln + 1;
					if (xmN > 96) xmN = 96;
					note.setIndex(xmN);
				}
			}

			// Bit 1: read instrument
			if (mask & 2) {
				var instValue = file.readUbyte();
				lastInst[channel] = instValue;
				if (note && instValue > 0) note.instrument = instValue;
			}

			// Bit 5: use last instrument
			if ((mask & 32) && note) {
				if (lastInst[channel] > 0) note.instrument = lastInst[channel];
			}

			// Bit 2: read volume/panning column
			if (mask & 4) {
				var volCol = file.readUbyte();
				lastVol[channel] = volCol;
				if (note) mapVolumeColumn(note, volCol);
			}

			// Bit 6: use last volume/panning
			if ((mask & 64) && note) {
				if (lastVol[channel]) mapVolumeColumn(note, lastVol[channel]);
			}

			// Bit 3: read effect + param
			if (mask & 8) {
				var effectType = file.readUbyte();
				var effectParam = file.readUbyte();
				lastEff[channel] = effectType;
				lastEffParam[channel] = effectParam;
				if (note) {
					var eff = mapEffect(effectType, effectParam);
					note.effect = eff.effect;
					note.param = eff.param;
				}
			}

			// Bit 7: use last effect + param
			if ((mask & 128) && note) {
				if (lastEff[channel]) {
					var eff2 = mapEffect(lastEff[channel], lastEffParam[channel]);
					note.effect = eff2.effect;
					note.param = eff2.param;
				}
			}
		}

		return patternData.slice(0, patternLength);
	}

	// ---- Volume column mapping ----
	// IT volume column per spec:
	// 0 = nothing
	// 1-64 = volume
	// 65-74 = fine volume up (equivalent to D0F, D1F, ...)
	// 75-84 = fine volume down (equivalent to DFx)
	// 85-94 = volume slide up (equivalent to Dx0)
	// 95-104 = volume slide down (equivalent to D0x)
	// 105-114 = pitch slide down (affects E/F memory, slide by x*4)
	// 115-124 = pitch slide up (affects E/F memory, slide by x*4)
	// 128-192 = panning (128=left, 160=center, 192=right)
	// 193-202 = portamento to (affects G memory)
	// 203-212 = vibrato (affects H/U memory)
	function mapVolumeColumn(note, vol) {
		if (vol === 0) return;
		if (vol >= 1 && vol <= 64) {
			// Set volume → XM volume column: volume + 16 (0x10 offset)
			note.volumeEffect = vol + 16;
		} else if (vol >= 65 && vol <= 74) {
			// Fine volume up → XM vol col: 0x90 + (vol - 65) = fine volslide up
			note.volumeEffect = 0x90 + (vol - 65);
		} else if (vol >= 75 && vol <= 84) {
			// Fine volume down → XM vol col: 0xA0 + (vol - 75)
			note.volumeEffect = 0xA0 + (vol - 75);
		} else if (vol >= 85 && vol <= 94) {
			// Volume slide up → XM vol col: 0x60 + (vol - 85)
			note.volumeEffect = 0x60 + (vol - 85);
		} else if (vol >= 95 && vol <= 104) {
			// Volume slide down → XM vol col: 0x70 + (vol - 95)
			note.volumeEffect = 0x70 + (vol - 95);
		} else if (vol >= 105 && vol <= 114) {
			// Pitch slide down → map to effect 2 (portamento down) with param*4
			// Store as volume effect for now (XM vol col pitch slide down)
			note.volumeEffect = 0x50 + (vol - 105);
		} else if (vol >= 115 && vol <= 124) {
			// Pitch slide up
			note.volumeEffect = 0x30 + (vol - 115);
		} else if (vol >= 128 && vol <= 192) {
			// Panning: 128=left(0), 160=center(128), 192=right(255)
			var pan = Math.round((vol - 128) / 64 * 255);
			// XM vol col panning: 0x80 + pan/16
			note.volumeEffect = 0x80 + Math.round(pan / 16);
		} else if (vol >= 193 && vol <= 202) {
			// Portamento to → XM vol col: 0xE0 + (vol - 193)
			note.volumeEffect = 0xE0 + (vol - 193);
		} else if (vol >= 203 && vol <= 212) {
			// Vibrato → XM vol col: 0xB0 + (vol - 203)
			note.volumeEffect = 0xB0 + (vol - 203);
		}
	}

	// ---- Effect mapping (IT → XM) ----
	// IT effects: 0=no effect, 1=A, 2=B, 3=C, ... 26=Z
	// A=portamento up, B=portamento down, C=tone portamento, D=vibrato,
	// E=tone portamento + vol slide, F=vibrato + vol slide, G=tremolo,
	// H=set panning, I=sample offset, J=volume slide, K=position jump,
	// L=pattern break, M=set tempo/BPM, N=set speed, O=set volume,
	// P=global volume, Q=extra fine portamento, R=panning slide,
	// S=Sxx special, T=set sample position, U=note delay, V=panning slide,
	// W=retrigger, X=tremor, Y=extra fine portamento, Z=set tempo
	function mapEffect(type, param) {
		var effect = { effect: 0, param: param };

		switch (type) {
			case 0: // no effect
				effect.effect = 0;
				break;
			case 1: // Axx = portamento up (XM effect 1)
				effect.effect = 1;
				break;
			case 2: // Bxx = portamento down (XM effect 2)
				effect.effect = 2;
				break;
			case 3: // Cxx = tone portamento (XM effect 3)
				effect.effect = 3;
				break;
			case 4: // Dxx = vibrato (XM effect 4)
				effect.effect = 4;
				break;
			case 5: // Exx = tone portamento + volume slide (XM effect 5)
				effect.effect = 5;
				break;
			case 6: // Fxx = vibrato + volume slide (XM effect 6)
				effect.effect = 6;
				break;
			case 7: // Gxx = tremolo (XM effect 7)
				effect.effect = 7;
				break;
			case 8: // Hxx = set panning (XM effect 8)
				effect.effect = 8;
				// IT panning: 0-64, 128=surround. Map to 0-255 for XM
				if (param <= 64) effect.param = Math.round(param / 64 * 255);
				else effect.param = 128; // surround → center
				break;
			case 9: // Ixx = sample offset (XM effect 9)
				effect.effect = 9;
				break;
			case 10: // Jxx = volume slide (XM effect 10/A)
				effect.effect = 10;
				break;
			case 11: // Kxx = position jump (XM effect 11/B)
				effect.effect = 11;
				break;
			case 12: // Lxx = pattern break (XM effect 13/D)
				effect.effect = 13;
				// IT param is a byte 0-63, XM uses BCD
				effect.param = ((param >> 4) * 10) + (param & 0xf);
				break;
			case 13: // Mxx = set tempo/BPM (XM effect 15/F)
				// IT: if param < 32 → speed, if >= 32 → BPM (same as XM Fxx)
				effect.effect = 15;
				break;
			case 14: // Nxx = set speed (same as Mxx with < 32)
				effect.effect = 15;
				break;
			case 15: // Oxx = set volume (not a standard XM effect, use volume column)
				// Store as volume effect
				effect.effect = 0;
				break;
			case 16: // Pxx = global volume (XM effect 16)
				effect.effect = 16;
				break;
			case 17: // Qxx = extra fine portamento
				effect = mapExtendedEffect(param);
				break;
			case 18: // Rxx = panning slide (XM effect 25/P)
				effect.effect = 25;
				break;
			case 19: // Sxx = special (XM effect 14/E)
				effect = mapSpecialEffect(param);
				break;
			case 20: // Txx = set sample position (fine offset)
				effect.effect = 9;
				break;
			case 21: // Uxx = note delay (handled via XM extended)
				effect.effect = 14;
				effect.param = 0xD0 | (param & 0xf);
				break;
			case 22: // Vxx = panning slide
				effect.effect = 25;
				break;
			case 23: // Wxx = retrigger (XM effect 27/R)
				effect.effect = 27;
				break;
			case 24: // Xxx = tremor (XM effect 29/T)
				effect.effect = 29;
				break;
			case 25: // Yxx = extra fine portamento
				if ((param & 0xF0) === 0x10) {
					effect.effect = 1;
					effect.param = 0xE0 | (param & 0xf); // extra fine up
				} else if ((param & 0xF0) === 0x20) {
					effect.effect = 2;
					effect.param = 0xE0 | (param & 0xf); // extra fine down
				}
				break;
			case 26: // Zxx = set tempo/BPM (same as Mxx)
				effect.effect = 15;
				break;
			default:
				if (type) console.warn("Unhandled IT effect: " + type + " param " + param.toString(16));
				break;
		}

		return effect;
	}

	function mapExtendedEffect(param) {
		// IT extended effects (Qxy, Rxy, etc. encoded in effect 17)
		var x = param >> 4;
		var y = param & 15;
		var effect = { effect: 0, param: param };

		switch (x) {
			case 0: // Q0y = set filter
				effect.effect = 0;
				break;
			case 1: // Q1y = fine portamento up
				effect.effect = 1;
				effect.param = 0xE0 | y;
				break;
			case 2: // Q2y = fine portamento down
				effect.effect = 2;
				effect.param = 0xE0 | y;
				break;
			case 3: // Q3y = glissando control
				effect.effect = 14;
				effect.param = 0x30 | y;
				break;
			case 4: // Q4y = vibrato waveform
				effect.effect = 14;
				effect.param = 0x40 | y;
				break;
			case 5: // Q5y = set finetune
				effect.effect = 14;
				effect.param = 0x50 | y;
				break;
			case 6: // Q6y = loop pattern
				effect.effect = 14;
				effect.param = 0x60 | y;
				break;
			case 7: // Q7y = tremolo waveform
				effect.effect = 14;
				effect.param = 0x70 | y;
				break;
			case 8: // Q8y = set panning (16 positions)
				effect.effect = 8;
				effect.param = y * 16;
				break;
			case 9: // Q9y = retrigger
				effect.effect = 27;
				break;
			case 10: // QAy = fine volume slide up
				effect.effect = 10;
				effect.param = 0xF0 | y;
				break;
			case 11: // QBy = fine volume slide down
				effect.effect = 10;
				effect.param = (y << 4) | 0xF;
				break;
			case 12: // QCy = note cut
				effect.effect = 14;
				effect.param = 0xC0 | y;
				break;
			case 13: // QDy = note delay
				effect.effect = 14;
				effect.param = 0xD0 | y;
				break;
			case 14: // QEy = pattern delay
				effect.effect = 14;
				effect.param = 0xE0 | y;
				break;
			case 15: // QFy = set active macro
				effect.effect = 0;
				break;
		}

		return effect;
	}

	function mapSpecialEffect(param) {
		// IT Sxx effects
		var x = param >> 4;
		var y = param & 15;
		var effect = { effect: 14, param: param };

		switch (x) {
			case 0: // S0y = set filter
				break;
			case 1: // S1y = glissando
				effect.param = 0x30 | y;
				break;
			case 2: // S2y = finetune
				effect.param = 0x50 | y;
				break;
			case 3: // S3y = vibrato waveform
				effect.param = 0x40 | y;
				break;
			case 4: // S4y = tremolo waveform
				effect.param = 0x70 | y;
				break;
			case 8: // S8y = panning
				effect.effect = 8;
				effect.param = y * 17;
				break;
			case 11: // SBy = pattern loop
				effect.param = 0x60 | y;
				break;
			case 12: // SCy = note cut
				effect.param = 0xC0 | y;
				break;
			case 13: // SDy = note delay
				effect.param = 0xD0 | y;
				break;
			case 14: // SEy = pattern delay
				effect.param = 0xE0 | y;
				break;
		}

		return effect;
	}

	// ---- Sample reading ----
	function readSample(file, offset) {
		var sample = Sample();
		if (!offset) return sample;

		file.goto(offset);

		// IT sample header (80 bytes)
		// Layout: sig(4) + filename(12) + 00h(1) + gvol(1) + flags(1) + dvol(1) +
		//         name(26) + cvt(1) + defpan(1) +
		//         length(4) + loopstart(4) + loopend(4) + c5spd(4) +
		//         susstart(4) + susend(4) + dataptr(4) +
		//         vibspeed(1) + vibdepth(1) + vibrate(1) + vibtype(1)
		var sig = file.readString(4); // "IMPS"
		sample.filename = file.readString(12); // DOS filename
		file.jump(1); // 00h (always zero)
		var globalVol = file.readUbyte(); // global volume (0-64)
		var flags = file.readUbyte(); // bit 0: on, 1: 16-bit, 2: stereo, 3: packed, 4: sustain loop, 5: pp sustain, 6: loop, 7: pp loop
		var defaultVolume = file.readUbyte();
		sample.name = file.readString(26); // 26 bytes
		var cvt = file.readUbyte(); // Cvt (convert) — bit 4 (0x10) = IT215
		var defaultPan = file.readUbyte();
		var sampleLength = file.readDWord();
		var loopStart = file.readDWord();
		var loopEnd = file.readDWord();
		var c5spd = file.readDWord();
		var sustainStart = file.readDWord();
		var sustainEnd = file.readDWord();
		var samplePointer = file.readDWord();
		var vibratoSpeed = file.readUbyte();
		var vibratoDepth = file.readUbyte();
		var vibratoRate = file.readUbyte();
		var vibratoType = file.readUbyte();

		sample.volume = defaultVolume;
		sample.c2spd = c5spd || 8363;
		sample.bits = (flags & 2) ? 16 : 8;
		sample.isStereo = !!(flags & 4);
		sample.length = sampleLength;

		// Loop
		// IT sample flags: bit 0=on, 1=16bit, 2=stereo, 3=packed,
		// 4=use loop, 5=use sustain loop, 6=ping-pong loop, 7=ping-pong sustain
		var hasLoop = (flags & 0x10) !== 0;
		var loopPingPong = (flags & 0x40) !== 0;
		var hasSustainLoop = (flags & 0x20) !== 0;
		var sustainPingPong = (flags & 0x80) !== 0;

		if (hasLoop) {
			sample.loop.enabled = true;
			sample.loop.start = loopStart;
			sample.loop.length = Math.max(0, loopEnd - loopStart);
			sample.loop.type = loopPingPong ? LOOPTYPE.PINGPONG : LOOPTYPE.FORWARD;
		} else if (hasSustainLoop) {
			sample.loop.enabled = true;
			sample.loop.start = sustainStart;
			sample.loop.length = Math.max(0, sustainEnd - sustainStart);
			sample.loop.type = sustainPingPong ? LOOPTYPE.PINGPONG : LOOPTYPE.FORWARD;
		}

		// Read sample data (delta-encoded, signed, or compressed)
		if (sampleLength > 0 && samplePointer > 0) {
			readSampleData(file, samplePointer, sample, flags, cvt);
		}

		// For 16-bit samples, IT stores length in samples (not bytes)
		// but some trackers store in bytes — handle both
		if (sample.bits === 16) {
			// IT spec: length is in samples (words), not bytes
			// Already handled in readSampleData
		}

		return sample;
	}

	function readSampleData(file, dataOffset, sample, flags, cvt) {
		var isCompressed = (flags & 8) !== 0;
		var is16bit = sample.bits === 16;
		var isStereo = sample.isStereo;
		var length = sample.length;

		if (isCompressed) {
			// IT 2.14/2.15 sample compression
			// Cvt bit 4 (0x10) = IT215 (double delta), otherwise IT214 (single delta)
			var isIT215 = (cvt & 0x10) !== 0;
			var decompressed = decompressITSample(file, dataOffset, length, is16bit, isIT215);
			for (var i = 0; i < decompressed.length; i++) {
				if (is16bit) {
					sample.data.push(decompressed[i] / 32768);
				} else {
					sample.data.push(decompressed[i] / 128);
				}
			}
		} else {
			// Uncompressed: IT uses delta-encoded signed samples (like XM)
			file.goto(dataOffset);
			var old = 0;
			var oldR = 0;

			if (is16bit) {
				for (var i = 0; i < length; i++) {
					var w = file.readShort() + old;
					if (w < -32768) w += 65536;
					else if (w > 32767) w -= 65536;
					old = w;
					sample.data.push(w / 32768);
					if (isStereo) {
						var wr = file.readShort() + oldR;
						if (wr < -32768) wr += 65536;
						else if (wr > 32767) wr -= 65536;
						oldR = wr;
						sample.data[i] = (sample.data[i] + wr / 32768) / 2;
					}
				}
			} else {
				for (i = 0; i < length; i++) {
					var b = file.readByte() + old;
					if (b < -128) b += 256;
					else if (b > 127) b -= 256;
					old = b;
					sample.data.push(b / 128);
					if (isStereo) {
						var br = file.readByte() + oldR;
						if (br < -128) br += 256;
						else if (br > 127) br -= 256;
						oldR = br;
						sample.data[i] = (sample.data[i] + br / 128) / 2;
					}
				}
			}
		}

		// Unroll ping-pong loops
		if (sample.loop.type === LOOPTYPE.PINGPONG && sample.loop.length > 0) {
			var loopPart = sample.data.slice(sample.loop.start, sample.loop.start + sample.loop.length);
			sample.data = sample.data.slice(0, sample.loop.start + sample.loop.length);
			sample.data = sample.data.concat(loopPart.reverse());
			sample.loop.length = sample.loop.length * 2;
			sample.length = sample.loop.start + sample.loop.length;
		}
	}

	// IT 2.14/2.15 sample decompression
	// Based on the OpenCP/xmp itsex.c implementation by Tammo Hinrichs (kb)
	function decompressITSample(file, dataOffset, len, is16bit, isIT215) {
		var output = new Array(len);
		var outputPos = 0;
		file.goto(dataOffset);

		while (len > 0) {
			// Read a block: 2-byte little-endian size, then data
			var blockSize = file.readWord();
			if (!blockSize) break;

			var blockData = file.readBytes(blockSize);
			// Bit reader state
			var bitPos = 0;     // bit position within current byte
			var byteIdx = 0;    // current byte index in block
			var bitsLeft = blockSize * 8; // total bits left in block

			function readBits(n) {
				var retval = 0;
				var offset = 0;
				while (n > 0) {
					if (bitsLeft <= 0) return retval;
					var m = n;
					if (m > (8 - bitPos)) m = 8 - bitPos;
					retval |= (blockData[byteIdx] >> bitPos & ((1 << m) - 1)) << offset;
					bitPos += m;
					n -= m;
					offset += m;
					bitsLeft -= m;
					if (bitPos >= 8) {
						bitPos = 0;
						byteIdx++;
					}
				}
				return retval;
			}

			var blklen = (len < (is16bit ? 0x4000 : 0x8000)) ? len : (is16bit ? 0x4000 : 0x8000);
			var blkpos = 0;
			var width = is16bit ? 17 : 9;
			var d1 = 0, d2 = 0;

			while (blkpos < blklen) {
				var value = readBits(width);

				if (width < 7) {
					// Method 1: 1-6 bits
					if (value === (1 << (width - 1))) {
						// "100..." → read new width (3 bits for 8-bit, 4 bits for 16-bit)
						value = readBits(is16bit ? 4 : 3) + 1;
						width = (value < width) ? value : value + 1;
						continue;
					}
				} else if (width < (is16bit ? 17 : 9)) {
					// Method 2: 7 to n-1 bits
					var border = (is16bit ? 0xFFFF : 0xFF) >> ((is16bit ? 17 : 9) - width);
					border -= (is16bit ? 8 : 4);
					if (value > border && value <= border + (is16bit ? 16 : 8)) {
						value -= border;
						width = (value < width) ? value : value + 1;
						continue;
					}
				} else if (width === (is16bit ? 17 : 9)) {
					// Method 3: n bits (9 for 8-bit, 17 for 16-bit)
					if (value & (is16bit ? 0x10000 : 0x100)) {
						width = (value + 1) & 0xff;
						continue;
					}
				} else {
					// Illegal width
					break;
				}

				// Expand value to signed
				var v;
				if (is16bit) {
					if (width < 16) {
						var shift = 16 - width;
						v = (value << shift) >> shift; // sign extend
					} else {
						v = (value << 16) >> 16; // sign extend 16-bit
					}
					d1 = (d1 + v) & 0xffff;
					d1 = (d1 << 16) >> 16; // wrap to signed 16-bit
					d2 = (d2 + d1) & 0xffff;
					d2 = (d2 << 16) >> 16; // wrap to signed 16-bit
					output[outputPos++] = isIT215 ? d2 : d1;
				} else {
					if (width < 8) {
						var shift8 = 8 - width;
						v = (value << shift8) >> shift8; // sign extend
					} else {
						v = (value << 24) >> 24; // sign extend byte
					}
					d1 = (d1 + v) & 0xff;
					d1 = (d1 << 24) >> 24; // wrap to signed 8-bit
					d2 = (d2 + d1) & 0xff;
					d2 = (d2 << 24) >> 24; // wrap to signed 8-bit
					output[outputPos++] = isIT215 ? d2 : d1;
				}
				blkpos++;
			}

			len -= blklen;
		}

		return output;
	}

	// ---- Instrument reading ----
	function readInstrument(file, offset, samples) {
		var instrument = Instrument();
		instrument.samples = [];
		instrument.sampleNumberForNotes = [];
		for (var i = 0; i < 96; i++) instrument.sampleNumberForNotes.push(0);

		if (!offset) {
			instrument.setSampleIndex(0);
			return instrument;
		}

		file.goto(offset);

		// IT instrument header (new format, cmwt >= 0x200)
		// Layout: sig(4) + filename(12) + 00h(1) + NNA(1) + DCT(1) + DCA(1) +
		//         FadeOut(2) + PPS(1) + PPC(1) + GbV(1) + DfP(1) + RV(1) + RP(1) +
		//         TrkVers(2) + NoS(1) + x(1) + name(26) + IFC(1) + IFR(1) +
		//         MCh(1) + MPr(1) + MIDIBnk(2) = 64 bytes before keyboard table
		var sig = file.readString(4); // "IMPI"
		instrument.filename = file.readString(12); // DOS filename
		file.jump(1); // 00h (always zero)
		var nna = file.readUbyte(); // new note action
		var dct = file.readUbyte(); // duplicate check type
		var dca = file.readUbyte(); // duplicate check action
		var fadeout = file.readWord(); // 16-bit fadeout
		var pitchPanSeparation = file.readByte();
		var pitchPanCenter = file.readUbyte();
		var globalVolume = file.readUbyte();
		var defaultPan = file.readUbyte();
		file.jump(2); // random volume/pan
		file.jump(2); // TrkVers
		file.jump(1); // NoS
		file.jump(1); // reserved
		instrument.name = file.readString(26);
		file.jump(1); // initial filter cutoff
		file.jump(1); // initial filter resonance
		file.jump(1); // MIDI channel
		file.jump(1); // MIDI program
		file.jump(2); // MIDI bank

		instrument.fadeout = fadeout || 128;

		// Keyboard table at offset 0x40 (64 bytes from start)
		// 120 bytes note→sample, 120 bytes note→note transposition
		for (i = 0; i < 120; i++) {
			var sampleIndex = file.readUbyte();
			if (i < 96) instrument.sampleNumberForNotes[i] = sampleIndex;
		}

		// We already read 120 bytes for sample map above.
		// Now read note map (120 bytes) — note transposition
		var noteMap = [];
		for (i = 0; i < 120; i++) {
			var noteTransp = file.readByte();
			noteMap.push(noteTransp);
		}

		// Volume envelope
		var volEnvFlags = file.readUbyte();
		var volEnvPoints = file.readUbyte(); // number of nodes
		var volEnvLoopStart = file.readUbyte();
		var volEnvLoopEnd = file.readUbyte();
		var volEnvSustainStart = file.readUbyte();
		var volEnvSustainEnd = file.readUbyte();

		// 25 volume envelope nodes (each: 1 byte Y, 2 bytes X/tick = 3 bytes)
		var volNodes = [];
		for (i = 0; i < 25; i++) {
			var volY = file.readByte(); // 0-64
			var volX = file.readWord(); // tick position
			volNodes.push([volX, volY]);
		}
		file.jump(1); // wasted byte

		// Panning envelope
		var panEnvFlags = file.readUbyte();
		var panEnvPoints = file.readUbyte();
		var panEnvLoopStart = file.readUbyte();
		var panEnvLoopEnd = file.readUbyte();
		var panEnvSustainStart = file.readUbyte();
		var panEnvSustainEnd = file.readUbyte();

		var panNodes = [];
		for (i = 0; i < 25; i++) {
			var panY = file.readByte(); // 0-64 (32=center)
			var panX = file.readWord();
			panNodes.push([panX, panY]);
		}
		file.jump(1); // wasted byte

		// Pitch envelope
		var pitchEnvFlags = file.readUbyte();
		var pitchEnvPoints = file.readUbyte();
		var pitchEnvLoopStart = file.readUbyte();
		var pitchEnvLoopEnd = file.readUbyte();
		var pitchEnvSustainStart = file.readUbyte();
		var pitchEnvSustainEnd = file.readUbyte();

		var pitchNodes = [];
		for (i = 0; i < 25; i++) {
			var pitchY = file.readByte();
			var pitchX = file.readWord();
			pitchNodes.push([pitchX, pitchY]);
		}
		file.jump(1); // wasted byte

		// Process volume envelope → XM format
		// IT envelope flags: bit 0 = on, bit 1 = loop, bit 2 = sustain loop
		if (volEnvFlags & 1) {
			instrument.volumeEnvelope.enabled = true;
			instrument.volumeEnvelope.points = volNodes.slice(0, volEnvPoints || 1);
			instrument.volumeEnvelope.count = volEnvPoints || 1;
			if (volEnvFlags & 4) instrument.volumeEnvelope.sustain = true;
			if (volEnvFlags & 2) instrument.volumeEnvelope.loop = true;
			instrument.volumeEnvelope.sustainPoint = volEnvSustainStart;
			instrument.volumeEnvelope.loopStartPoint = volEnvLoopStart;
			instrument.volumeEnvelope.loopEndPoint = volEnvLoopEnd;
		}

		// Process panning envelope → XM format
		if (panEnvFlags & 1) {
			instrument.panningEnvelope.enabled = true;
			instrument.panningEnvelope.points = panNodes.slice(0, panEnvPoints || 1);
			instrument.panningEnvelope.count = panEnvPoints || 1;
			if (panEnvFlags & 4) instrument.panningEnvelope.sustain = true;
			if (panEnvFlags & 2) instrument.panningEnvelope.loop = true;
			instrument.panningEnvelope.sustainPoint = panEnvSustainStart;
			instrument.panningEnvelope.loopStartPoint = panEnvLoopStart;
			instrument.panningEnvelope.loopEndPoint = panEnvLoopEnd;
		}

		// Build samples list from referenced sample indices
		var usedSampleIndices = {};
		for (i = 0; i < 96; i++) {
			var si = instrument.sampleNumberForNotes[i];
			if (si > 0) usedSampleIndices[si - 1] = true;
		}

		var sampleList = [];
		var sampleMap = {}; // original sample index → position in instrument.samples
		for (i = 0; i < samples.length; i++) {
			if (usedSampleIndices[i]) {
				sampleMap[i] = sampleList.length;
				sampleList.push(samples[i]);
			}
		}

		// If no samples referenced, add an empty one
		if (sampleList.length === 0) {
			sampleList.push(Sample());
		}

		instrument.samples = sampleList;
		instrument.sample = sampleList[0];

		// Remap sampleNumberForNotes to local indices
		for (i = 0; i < 96; i++) {
			var origIdx = instrument.sampleNumberForNotes[i];
			if (origIdx > 0 && sampleMap[origIdx - 1] !== undefined) {
				instrument.sampleNumberForNotes[i] = sampleMap[origIdx - 1];
			} else {
				instrument.sampleNumberForNotes[i] = 0;
			}
		}

		instrument.setSampleIndex(0);
		return instrument;
	}

	function getEmptyRow(channelCount) {
		var row = [];
		for (var i = 0; i < channelCount; i++) row.push(Note());
		return row;
	}

	return me;
};

export default ImpulseTracker;
